/**
 * The draft comments' shared pieces, used by both sides so they look and behave the same:
 *   - DraftFeedbackView (the person the draft is shared with, in Version Compare → Draft Feedback)
 *   - DraftCommentsPanel (the owner / delegate, in the editor)
 *
 *   CommentsToggle — the blue 💬 button on a commented sentence: folds / unfolds its comments, shows how many there
 *                    are (when more than one) and an orange dot when one of them is not sent yet
 *   ThreadCard     — one comment: the thin left rule, "Name · time · edited" (or "You" + "Not sent"), the actions at the
 *                    right (each side passes its own: Reply / Edit / Delete, or Resolve) and the text
 *   formatWhen     — the comments' date, e.g. "Nov 4, 7:28 AM"
 */

import React from 'react';
import PropTypes from 'prop-types';
import { MessageOutlined } from '@ant-design/icons';
import { SbTooltip, TooltipTriggerStyled } from 'uiCore';
import { EDITED_LABEL, HIDE_COMMENTS_TOOLTIP, NOT_SUBMITTED_LABEL, getShowCommentsTooltip } from 'Utils/draftFeedback';
import {
  CommentBadgeStyled, NotSentTagStyled, ThreadActionsStyled, ThreadCardStyled, ThreadHeaderStyled, ThreadMetaStyled,
  ThreadTextStyled, UnsentDotStyled,
} from './DraftFeedback.styles';

export const formatWhen = (iso) => {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime())
    ? when.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
};

// How many comments (roots and replies) a sentence's threads hold, and whether one of them is not sent yet
export const countThreads = (threads) => ({
  count: (threads || []).reduce((n, t) => n + 1 + (t.replies || []).length, 0),
  unsent: (threads || []).some((t) => t.root.pending || (t.replies || []).some((r) => r.pending)),
});

export const CommentsToggle = ({ count, unsent = false, open, onToggle }) => {
  const label = open ? HIDE_COMMENTS_TOOLTIP : getShowCommentsTooltip(count);
  return (
    <SbTooltip content={label}>
      <TooltipTriggerStyled>
        <CommentBadgeStyled type="button" aria-expanded={open} aria-label={label} data-testid="comments-toggle"
          onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          <MessageOutlined />
          {count > 1 && <span>{count}</span>}
          {unsent && <UnsentDotStyled data-testid="unsent-dot" />}
        </CommentBadgeStyled>
      </TooltipTriggerStyled>
    </SbTooltip>
  );
};

CommentsToggle.propTypes = {
  count: PropTypes.number.isRequired,
  unsent: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// testId: each side's own (the tests tell the two apart); actions: the buttons at the right of the header
// compact: the owner's editor — one thin line: who · when, the comment (wraps only when long), the actions at the right
export const ThreadCard = ({ comment, isReply = false, actions = null, testId = undefined, children = null,
  compact = false }) => (compact ? (
  <ThreadCardStyled $pending={comment.pending} $reply={isReply} $compact data-testid={testId}>
    <ThreadMetaStyled>
      <b>{comment.author_name || comment.author_email}</b>
      {formatWhen(comment.created_at) && ` · ${formatWhen(comment.created_at)}`}
    </ThreadMetaStyled>
    <ThreadTextStyled>{comment.text}</ThreadTextStyled>
    {actions && <ThreadActionsStyled>{actions}</ThreadActionsStyled>}
  </ThreadCardStyled>
) : (
  <ThreadCardStyled $pending={comment.pending} $reply={isReply} data-testid={testId}>
    <ThreadHeaderStyled>
      <span>
        <b>{comment.pending ? 'You' : comment.author_name || comment.author_email}</b>
        {!comment.pending && formatWhen(comment.created_at) && ` · ${formatWhen(comment.created_at)}`}
        {comment.edited_at && ` · ${EDITED_LABEL}`}
        {comment.pending && <NotSentTagStyled>{NOT_SUBMITTED_LABEL}</NotSentTagStyled>}
      </span>
      {actions && <ThreadActionsStyled>{actions}</ThreadActionsStyled>}
    </ThreadHeaderStyled>
    <div>{comment.text}</div>
    {children}
  </ThreadCardStyled>
));

ThreadCard.propTypes = {
  comment: PropTypes.shape({
    pending: PropTypes.bool, author_name: PropTypes.string, author_email: PropTypes.string, created_at: PropTypes.string,
    edited_at: PropTypes.string, text: PropTypes.string,
  }).isRequired,
  isReply: PropTypes.bool,
  actions: PropTypes.node,
  testId: PropTypes.string,
  children: PropTypes.node,
  compact: PropTypes.bool,
};
